import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { obtenerAuditLog } from "@/lib/api";
import { useExpediente } from "@/hooks/useExpediente";
import type { EventoAuditoriaItem } from "@/lib/api";
import { Header } from "@/components/Header";
import { ChatPanel } from "@/components/ChatPanel";
import { PlaceholdersPendientes } from "@/components/PlaceholdersPendientes";
import {
  CartaProyectadaCalm,
  nombrePlantilla,
} from "@/components/CartaProyectada";
import {
  AnalisisSustantivo,
  AuditoriaLLMPanel,
  AuditoriaTipologia,
  Linea,
  PanelReiteracion,
} from "@/components/PanelesAnalisis";
import { AccionesBar } from "@/components/AccionesBar";
import { useDescargaCarta } from "@/hooks/useDescargaCarta";
import {
  formatearCop,
  formatearFechaCorta,
  formatearKwh,
} from "@/lib/utils";
import type { ExpedienteAnalizado } from "@/types";

export function ExpedienteDetailPage() {
  const { id = "" } = useParams<{ id: string }>();

  // Estado compartido del chat flotante: el click en una fila de
  // "Datos pendientes" abre el chat con el input pre-llenado.
  const [chatAbierto, setChatAbierto] = useState(false);
  const [chatPrefill, setChatPrefill] = useState<string | null>(null);

  const abrirChatConPrefill = (texto: string) => {
    setChatPrefill(texto);
    setChatAbierto(true);
  };

  const { data, isLoading, error } = useExpediente(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-slate-500">
          Cargando expediente…
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-rose-600">
          {(error as Error)?.message || "No se encontró el expediente."}
        </div>
      </div>
    );
  }

  const arts = data.decision?.decision.articulos.join(" · ") ?? "—";
  const alertasCount = data.decision?.metadata.alertas?.length ?? 0;
  const reiteracionDetectada = !!data.analisis_reiteracion?.detectada;
  const reiteracionPatron = !!data.analisis_reiteracion?.patron_reincidencia;
  const visitaRequerida = !!data.recomendacion_visita?.requiere_visita;
  const tieneSustantivo = data.analisis_sustantivo != null;
  const audit = data.auditoria_llm;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />

      {/* Sticky actions — radicado + estado + Aprobar / Rechazar / Descargar */}
      <StickyActions expediente={data} />

      <main
        className="mx-auto"
        style={{ maxWidth: 1040, padding: "20px 32px 120px" }}
      >
        <div className="crumbs">
          <Link to="/">Pendientes CRM</Link>
          <span className="sep">/</span>
          <Link to="/expedientes">Bandeja</Link>
          <span className="sep">/</span>
          <span className="mono">{data.numero_radicado}</span>
        </div>

        <BannerTextoPobre expediente={data} />

        {/* Hero decision: lo primero que el operador mira */}
        <HeroDecisionPanel expediente={data} />

        {/* Tira de contexto del cliente */}
        <ContextoStrip expediente={data} />

        {/* Reclamo del cliente — texto íntegro tal cual lo aporta el
            CRM/PDF para que el operador tenga la cita textual a la mano. */}
        <ReclamoDelCliente expediente={data} />

        {/* Resumen del análisis del motor — qué decidió GEMA y por qué,
            antes de la carta proyectada completa. */}
        <ResumenAnalisis expediente={data} />

        {/* CARTA proyectada — visible a primera vista (estilo documento legal) */}
        {data.proyeccion_carta && (
          <section style={{ marginBottom: 32 }}>
            <CartaProyectadaCalm expediente={data} />
            <PlaceholdersPendientes
              expediente={data}
              onAbrirChat={abrirChatConPrefill}
            />
          </section>
        )}

        {/* Sección: Señales del motor */}
        <div className="section-group">
          <h2 className="section-group__h">Señales del motor</h2>
          <p className="section-group__sub">
            Las cosas que el motor quiere que mires antes de decidir.
          </p>
          <div>
            <Disclosure
              title="Discrepancia con el CRM"
              sub="el motor reclasificó la pretensión"
              signal={{
                color: "amber",
                label: `conf ${Math.round(
                  (data.decision?.metadata.confianza_motor ?? 0) * 100,
                )}%`,
              }}
              defaultOpen={false}
            >
              <AuditoriaTipologia expediente={data} />
            </Disclosure>

            <Disclosure
              title="Reiteración del cliente"
              sub={
                reiteracionDetectada
                  ? `${(data.analisis_reiteracion?.antecedentes?.length ?? 0)} antecedente(s) en 6 meses`
                  : "Sin antecedentes en los últimos 6 meses"
              }
              signal={
                reiteracionPatron
                  ? { color: "violet", label: "patrón ≥ 3" }
                  : reiteracionDetectada
                    ? { color: "amber", label: `score ${data.analisis_reiteracion?.score ?? "—"}` }
                    : undefined
              }
              defaultOpen={reiteracionDetectada}
            >
              <PanelReiteracion expediente={data} />
            </Disclosure>

            <Disclosure
              title="Visita técnica recomendada"
              sub={visitaRequerida ? "AVM sugerida antes de notificar" : "No requerida"}
              signal={visitaRequerida ? { color: "orange", label: "AVM" } : undefined}
              defaultOpen={visitaRequerida}
            >
              <RecomendacionVisitaTecnica expediente={data} />
            </Disclosure>

            <Disclosure
              title="Auditor LLM"
              sub="2.ª opinión sobre la decisión"
              signal={
                audit
                  ? {
                      color: "violet",
                      label: `conf ${Math.round((audit.confianza_propia ?? 0) * 100)}%`,
                    }
                  : undefined
              }
              defaultOpen={false}
            >
              <AuditoriaLLMPanel expediente={data} />
            </Disclosure>

            {tieneSustantivo && (
              <Disclosure
                title="Análisis sustantivo paralelo (SAP)"
                sub="cuando la decisión se resolvió por Art. 158/154"
                signal={{ color: "green", label: "auditoría" }}
                defaultOpen={false}
              >
                <AnalisisSustantivo expediente={data} />
              </Disclosure>
            )}

            <Disclosure
              title="Resumen jurídico del motor"
              sub={`${alertasCount} alerta(s) · Art. ${arts}`}
              signal={
                alertasCount > 0
                  ? { color: "amber", label: `${alertasCount} alertas` }
                  : undefined
              }
              defaultOpen={false}
            >
              <ResumenDecision expediente={data} />
            </Disclosure>
          </div>
        </div>

        {/* Sección: Trazabilidad */}
        <div className="section-group">
          <h2 className="section-group__h">Trazabilidad</h2>
          <p className="section-group__sub">
            Quién hizo qué y la cadena legal hacia la Superintendencia.
          </p>
          <div>
            <Disclosure
              title="Historial de acciones"
              sub={`${(data.historial_acciones ?? []).length} eventos del revisor`}
              defaultOpen={false}
            >
              <Historial expediente={data} />
            </Disclosure>

            <Disclosure
              title="Audit log SSPD"
              sub="cadena de hashes inmutable"
              signal={{ color: "green", label: "verificada" }}
              defaultOpen={false}
            >
              <AuditLogSSPD expedienteId={data.expediente_id} />
            </Disclosure>
          </div>
        </div>
      </main>

      {/* Chat FAB negro estilo diseño */}
      <ChatFloating
        expediente={data}
        abierto={chatAbierto}
        setAbierto={setChatAbierto}
        prefill={chatPrefill}
        onPrefillUsado={() => setChatPrefill(null)}
      />
    </div>
  );
}

// ─── Sticky Actions (radicado + estado + acciones primarias) ─── //

function StickyActions({ expediente }: { expediente: ExpedienteAnalizado }) {
  const { descargar: onDescargar, descargando } = useDescargaCarta(
    expediente.expediente_id,
    `GEMA_${expediente.empresa}_${expediente.numero_radicado}.docx`,
  );

  const estadoLabel =
    {
      EN_ANALISIS: "En análisis",
      PENDIENTE_INFO: "Pendiente info",
      PENDIENTE_REVISION: "Pendiente revisión",
      EN_AJUSTE: "En ajuste",
      APROBADO: "Aprobado",
      RECHAZADO: "Rechazado",
      NOTIFICADO: "Notificado",
    }[expediente.estado] ?? expediente.estado;

  const estadoPin =
    expediente.estado === "APROBADO"
      ? "pin--green"
      : expediente.estado === "RECHAZADO"
        ? "pin--red"
        : expediente.estado === "PENDIENTE_INFO"
          ? "pin--orange"
          : "pin--amber";

  return (
    <div className="sticky-actions" style={{ marginLeft: 0, marginRight: 0 }}>
      <div style={{ paddingLeft: 32 }}>
        <div className="row row-2">
          <span className="sticky-actions__id">{expediente.numero_radicado}</span>
          <span className={`pin ${estadoPin}`} style={{ width: 6, height: 6, borderRadius: 999, display: "inline-block" }} />
          <span className="sticky-actions__status">{estadoLabel}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
          {expediente.empresa} · NIC <span className="mono">{expediente.nic}</span>
          {expediente.pqr_input?.direccion ? ` · ${expediente.pqr_input.direccion}` : ""}
        </div>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8, paddingRight: 32, alignItems: "center" }}>
        {expediente.decision && (
          <button
            onClick={onDescargar}
            disabled={descargando}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "0 16px",
              height: 34,
              background: "var(--emerald-deep)",
              color: "white",
              border: 0,
              borderRadius: "var(--r-sm)",
              fontSize: 13,
              fontWeight: 600,
              cursor: descargando ? "wait" : "pointer",
              boxShadow: "0 1px 2px oklch(0.30 0.08 162 / 0.20)",
              opacity: descargando ? 0.7 : 1,
              transition: "background .14s",
            }}
            onMouseEnter={(e) => {
              if (!descargando) e.currentTarget.style.background = "oklch(0.30 0.08 162)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--emerald-deep)";
            }}
            title="Descarga la carta en formato Word con la plantilla y placeholders rellenos"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
              <path d="M3 10v2.5A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5V10M8 2v8M5 7l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {descargando ? "Generando…" : "Descargar"}
          </button>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <AccionesBarCompact expediente={expediente} />
        </div>
      </div>
    </div>
  );
}

// AccionesBar embebido en el sticky — reutiliza el componente real.
function AccionesBarCompact({ expediente }: { expediente: ExpedienteAnalizado }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <AccionesBar expediente={expediente} />
    </div>
  );
}

// ─── HeroDecisionPanel ──────────────────────────────────────── //

function HeroDecisionPanel({ expediente }: { expediente: ExpedienteAnalizado }) {
  const dec = expediente.decision?.decision.codigo_resolucion;
  const arts = expediente.decision?.decision.articulos.join(" · ") ?? "—";
  const conf = Math.round(
    (expediente.decision?.metadata.confianza_motor ?? 0) * 100,
  );
  const plantilla = expediente.decision?.metadata.plantilla_id;
  const alertas = expediente.decision?.metadata.alertas?.length ?? 0;
  const audit = expediente.auditoria_llm;

  if (!dec) {
    return (
      <div className="hero-decision" style={{ marginBottom: 32 }}>
        <div>
          <div className="hero-decision__label">El motor está analizando</div>
          <h1 className="hero-decision__title">En análisis…</h1>
          <div className="hero-decision__meta">
            <span>Aún sin decisión.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-decision" style={{ marginBottom: 32 }}>
      <div>
        <div className="hero-decision__label">El motor propone</div>
        <h1 className="hero-decision__title">
          {dec.replace("_", " ")}
          <span className="article">— Art. {arts}</span>
        </h1>
        <div className="hero-decision__meta">
          {plantilla && (
            <>
              <span>{nombrePlantilla(plantilla)}</span>
              <span className="dot" />
            </>
          )}
          <span>{alertas} alerta(s) activas</span>
          {audit && (
            <>
              <span className="dot" />
              <span>
                Auditor LLM{" "}
                <strong style={{ fontWeight: 500, color: "var(--ink)" }}>
                  conf {Math.round((audit.confianza_propia ?? 0) * 100)} %
                </strong>
              </span>
            </>
          )}
        </div>
      </div>
      <div className="hero-decision__confidence">
        <div className="lbl">Confianza del motor</div>
        <div className="num">
          {conf}
          <span style={{ fontSize: 18, color: "var(--ink-3)" }}> %</span>
        </div>
        <div className="conf-line" style={{ width: "100%" }}>
          <div className="bar" style={{ maxWidth: "none", flex: 1 }}>
            <div
              className="fill"
              style={{
                width: `${conf}%`,
                background: conf < 70 ? "var(--amber)" : "var(--ink)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ContextoStrip (tira de 6 columnas con datos clave) ──────── //

function ContextoStrip({ expediente }: { expediente: ExpedienteAnalizado }) {
  const pqr = expediente.pqr_input;
  if (!pqr) return null;
  const periodo = pqr.fecha_factura_reclamada
    ? formatearFechaCorta(pqr.fecha_factura_reclamada)
    : "—";
  const consumo = pqr.consumo_reclamado_kwh
    ? formatearKwh(pqr.consumo_reclamado_kwh)
    : "—";
  const items: Array<{ l: string; v: string; mono?: boolean }> = [
    { l: "Cliente", v: expediente.placeholders_carta?.nombre_usuario || "—" },
    { l: "NIC", v: expediente.nic, mono: true },
    { l: "Período", v: periodo },
    { l: "Estrato", v: String(pqr.estrato ?? "—") },
    { l: "Consumo", v: consumo, mono: true },
    { l: "Ruta", v: pqr.ruta_logica },
  ];
  return (
    <div
      className="contexto-strip"
      style={{
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        marginBottom: 32,
      }}
    >
      {items.map((it) => (
        <div key={it.l} className="contexto-strip__cell">
          <div className="contexto-strip__lbl">{it.l}</div>
          <div
            className="contexto-strip__val"
            style={{ fontFamily: it.mono ? "var(--font-mono)" : "inherit" }}
          >
            {it.v}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ReclamoDelCliente ─────────────────────────────────────────── //
// Cita textual del motivo del reclamo tal como lo aportó el CRM /
// PDF. Se muestra en bloque serif para que el operador tenga la
// referencia exacta a la mano antes de revisar la carta proyectada.

function ReclamoDelCliente({ expediente }: { expediente: ExpedienteAnalizado }) {
  const motivo = expediente.pqr_input?.motivo_reclamacion?.trim();
  if (!motivo) return null;
  return (
    <section style={{ marginBottom: 32 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Reclamo del cliente
      </div>
      <blockquote
        style={{
          margin: 0,
          padding: "20px 24px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderLeft: "3px solid var(--ink-3)",
          borderRadius: "var(--r-md)",
          fontFamily: "var(--font-serif)",
          fontSize: 15,
          lineHeight: 1.65,
          color: "var(--ink)",
          fontStyle: "italic",
          whiteSpace: "pre-wrap",
        }}
      >
        {motivo}
      </blockquote>
    </section>
  );
}

// ─── ResumenAnalisis ──────────────────────────────────────────── //
// Panel breve con lo que el motor decidió, los considerandos clave y
// las alertas activas. Se muestra antes de la carta proyectada para
// que el operador entienda en 5 segundos qué hizo GEMA y por qué,
// sin tener que leer la carta completa.

function ResumenAnalisis({ expediente }: { expediente: ExpedienteAnalizado }) {
  const dec = expediente.decision;
  if (!dec) return null;

  // Extraer 2-3 considerandos clave del fundamento del motor. El
  // fundamento es un string largo: lo partimos por oraciones y nos
  // quedamos con las más densas (>40 chars).
  const fundamento = dec.decision.fundamento ?? "";
  const oraciones = fundamento
    .split(/(?<=\.)\s+/)
    .map((o) => o.trim())
    .filter((o) => o.length > 40)
    .slice(0, 3);

  const alertas = Array.from(new Set(dec.metadata.alertas ?? []));
  const arts = dec.decision.articulos.join(" · ");
  const r = dec.reliquidacion;
  const valorAFavor =
    r && r.valor_a_favor_cop ? formatearCop(r.valor_a_favor_cop) : null;

  return (
    <section style={{ marginBottom: 32 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Resumen del análisis
      </div>
      <div
        style={{
          padding: "20px 24px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-md)",
        }}
      >
        {/* Linea-1: decisión + arts + (reliquidación) */}
        <div
          className="row row-3"
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: 17,
            fontWeight: 500,
            color: "var(--ink)",
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span>
            El motor concluyó{" "}
            <strong style={{ color: "var(--ink)" }}>
              {dec.decision.codigo_resolucion.replace("_", " ")}
            </strong>
          </span>
          <span className="muted" style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>
            Art. {arts}
          </span>
          {valorAFavor && (
            <span
              className="badge badge--green"
              style={{ fontFamily: "var(--font-sans)" }}
            >
              <span className="dot" />
              {valorAFavor} a favor
            </span>
          )}
        </div>

        {/* Considerandos clave */}
        {oraciones.length > 0 && (
          <ul
            style={{
              margin: "0 0 14px 0",
              padding: 0,
              listStyle: "none",
            }}
          >
            {oraciones.map((o, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: "6px 0",
                  fontSize: 13.5,
                  color: "var(--ink-2)",
                  lineHeight: 1.55,
                }}
              >
                <span
                  style={{
                    marginTop: 8,
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: "var(--ink-3)",
                    flexShrink: 0,
                  }}
                />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Alertas activas (si las hay) */}
        {alertas.length > 0 && (
          <div
            style={{
              paddingTop: 12,
              borderTop: "1px solid var(--border)",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <span
              className="eyebrow"
              style={{ marginRight: 6, alignSelf: "center" }}
            >
              Alertas
            </span>
            {alertas.map((a) => (
              <span
                key={a}
                className="badge badge--amber"
                style={{ fontSize: 11 }}
              >
                <span className="dot" />
                {a.replace(/_/g, " ").toLowerCase()}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── CartaProyectadaCalm (documento legal visible) ───────────── //






// ─── Disclosure (calm pattern del diseño: chevron + pin de señal) ─ //

type PinColor = "amber" | "red" | "violet" | "orange" | "green" | "ink";

function Disclosure({
  title,
  sub,
  signal,
  defaultOpen,
  children,
}: {
  title: string;
  sub?: string;
  signal?: { color: PinColor; label: string };
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={"disclosure" + (open ? " is-open" : "")}>
      <button
        type="button"
        className="disclosure__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          border: 0,
          padding: 0,
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span className="disclosure__chevron">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 2l4 4-4 4" />
          </svg>
        </span>
        <span className="disclosure__title">{title}</span>
        {sub && <span className="disclosure__sub">{sub}</span>}
        {signal && (
          <span className="disclosure__signal">
            <span className={`pin pin--${signal.color}`} />
            {signal.label}
          </span>
        )}
      </button>
      <div className="disclosure__body">{children}</div>
    </div>
  );
}

// ─── Chat flotante (botón + panel deslizable) ──────────────────── //

function ChatFloating({
  expediente,
  abierto,
  setAbierto,
  prefill,
  onPrefillUsado,
}: {
  expediente: ExpedienteAnalizado;
  abierto: boolean;
  setAbierto: (v: boolean) => void;
  prefill?: string | null;
  onPrefillUsado?: () => void;
}) {
  return (
    <>
      {!abierto && (
        <button
          onClick={() => setAbierto(true)}
          className="chat-fab"
          title="Preguntar a GEMA"
        >
          <span className="chat-fab__g">G</span>
          Preguntar a GEMA
        </button>
      )}
      {abierto && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 420,
            maxWidth: "calc(100vw - 48px)",
            height: "min(680px, calc(100vh - 100px))",
            zIndex: 60,
            boxShadow: "var(--shadow-lg)",
            borderRadius: "var(--r-lg)",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            className="card__head"
            style={{ borderBottom: "1px solid var(--border)" }}
          >
            <div className="brand-mark" style={{ width: 22, height: 22, fontSize: 10 }}>
              G
            </div>
            <h3 style={{ flex: 1 }}>Chat con GEMA</h3>
            <button
              onClick={() => setAbierto(false)}
              className="btn btn--ghost btn--sm"
              title="Cerrar"
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
            <ChatPanel
              expediente={expediente}
              prefill={prefill ?? undefined}
              onPrefillUsado={onPrefillUsado}
              onClickPendiente={(label) => {
                /* No-op: el prefill ya entra por la prop. Esta firma
                   solo está para evitar que ChatPanel intente abrir su
                   propio FAB cuando se hace click en chips internos. */
                void label;
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ResumenPQR antiguo — el ContextoStrip lo reemplaza visualmente, pero
// se mantiene aquí por compatibilidad si algún flujo lo invoca.

function ResumenDecision({ expediente }: { expediente: ExpedienteAnalizado }) {
  const dec = expediente.decision;
  if (!dec)
    return (
      <div className="card p-4 text-sm text-slate-500">
        No hay decisión: {expediente.motivo_cierre || "datos insuficientes."}
      </div>
    );
  const r = dec.reliquidacion;
  return (
    <div className="card p-4 space-y-2 text-sm">
      <h3 className="font-semibold text-slate-700">Decisión del motor</h3>
      <Linea k="Resolución" v={dec.decision.codigo_resolucion} />
      <Linea k="Artículos" v={dec.decision.articulos.join(", ") || "—"} />
      <Linea k="Plantilla" v={dec.metadata.plantilla_id} />
      <Linea
        k="Confianza"
        v={`${(dec.metadata.confianza_motor * 100).toFixed(0)} %`}
      />
      {r && (
        <div className="pt-2 border-t border-slate-100 space-y-1">
          <Linea
            k="Original"
            v={`${formatearKwh(r.consumo_original_kwh)} (${formatearCop(r.valor_original_cop)})`}
          />
          <Linea
            k="Reliquidado"
            v={`${formatearKwh(r.consumo_reliquidado_kwh)} (${formatearCop(r.valor_reliquidado_cop)})`}
          />
          <Linea
            k="A favor"
            v={formatearCop(r.valor_a_favor_cop)}
            destacar={(r.valor_a_favor_cop ?? 0) > 0}
          />
        </div>
      )}
      {dec.metadata.alertas.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs uppercase text-slate-500 mb-1">Alertas</p>
          <ul className="list-disc list-inside text-amber-700 text-xs">
            {Array.from(new Set(dec.metadata.alertas)).map((a) => (
              <li key={a}>{a.replace(/_/g, " ").toLowerCase()}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[10px] text-slate-400 italic pt-1">
        El fundamento textual se desarrolla en la carta proyectada (centro).
      </p>
    </div>
  );
}


function BannerTextoPobre({ expediente }: { expediente: ExpedienteAnalizado }) {
  const motivo = expediente.pqr_input?.motivo_reclamacion ?? "";
  const observacion = expediente.observacion_crm_original ?? "";
  // v0.10.55 — evaluamos el MEJOR texto disponible: si la observación
  // del CRM (campo `pqr_unificada.observacion`) trae más contenido que
  // el motivo procesado, ese es el texto representativo del reclamo.
  // Evita falsos positivos como RE4110202627538 donde el motivo quedó
  // corto pero la BD sí trae el alegato del cliente.
  const textoEvaluable =
    observacion.trim().length > motivo.trim().length ? observacion : motivo;
  const limpio = textoEvaluable.trim().toLowerCase();
  const esPobre =
    limpio.length < 60 ||
    limpio === "" ||
    /^asunto:\s*(reclamo|recurso)\.?\s*(en curso\.?)?$/i.test(
      textoEvaluable.trim(),
    );
  if (!esPobre) return null;
  const params = new URLSearchParams({
    numero_radicado: expediente.numero_radicado,
    nic: expediente.nic,
    empresa: expediente.empresa,
  });
  return (
    <div className="card p-4 border-amber-300 bg-amber-50 flex items-start gap-3">
      <span className="text-2xl">📄</span>
      <div className="flex-1 text-sm">
        <p className="font-semibold text-amber-900">
          El texto del reclamo es insuficiente para clasificar con detalle
        </p>
        <p className="text-amber-800 text-xs mt-1">
          GEMA solo recibió{" "}
          <span className="font-mono bg-amber-100 px-1 rounded">
            {motivo.trim() || "(vacío)"}
          </span>
          . Para que el motor clasifique con confianza alta y haga el
          análisis del fondo correctamente, sube el PDF físico del
          radicado: la visión del modelo extrae el cuerpo del reclamo.
        </p>
      </div>
      <Link
        to={`/expedientes/nuevo?${params.toString()}`}
        className="btn-primary whitespace-nowrap"
      >
        📎 Subir PDF
      </Link>
    </div>
  );
}

function Historial({ expediente }: { expediente: ExpedienteAnalizado }) {
  if (expediente.historial_acciones.length === 0) return null;
  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">
        Historial de acciones
      </h3>
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase">
          <tr>
            <th className="text-left py-1">Fecha</th>
            <th className="text-left py-1">Tipo</th>
            <th className="text-left py-1">Autor</th>
            <th className="text-left py-1">Resultado</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {expediente.historial_acciones.map((a) => (
            <tr key={a.id}>
              <td className="py-1">{formatearFechaCorta(a.timestamp)}</td>
              <td className="py-1 font-medium">{a.tipo}</td>
              <td className="py-1">{a.autor}</td>
              <td className="py-1 text-slate-600">
                {Object.keys(a.resultado).length > 0
                  ? JSON.stringify(a.resultado)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecomendacionVisitaTecnica({
  expediente,
}: {
  expediente: ExpedienteAnalizado;
}) {
  const rec = expediente.recomendacion_visita;
  if (!rec || !rec.requiere_visita) return null;
  return (
    <div style={{ maxWidth: 720 }}>
      <p style={{ margin: "0 0 18px", fontSize: 14, color: "var(--ink-2)" }}>
        Antes de cerrar, el motor sugiere ordenar visita técnica. {rec.motivos.length} razón
        {rec.motivos.length === 1 ? "" : "es"}:
      </p>
      <div className="stack stack-3">
        {rec.motivos.map((m) => (
          <div
            key={m.codigo}
            style={{ display: "flex", gap: 14, alignItems: "flex-start" }}
          >
            <span
              style={{
                marginTop: 6,
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--orange)",
                flexShrink: 0,
              }}
            />
            <div>
              <div
                className="mono"
                style={{
                  fontWeight: 500,
                  fontSize: 12,
                  letterSpacing: "0.02em",
                  color: "var(--ink-2)",
                }}
              >
                {m.codigo}
                <span className="muted" style={{ marginLeft: 8, fontFamily: "inherit", fontWeight: 400 }}>
                  · {m.norma}
                </span>
              </div>
              <div
                style={{
                  color: "var(--ink-2)",
                  fontSize: 13.5,
                  marginTop: 4,
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                }}
              >
                {m.detalle}
              </div>
            </div>
          </div>
        ))}
      </div>
      {rec.tipo_orden_sugerida && (
        <div
          style={{
            marginTop: 18,
            padding: "10px 14px",
            background: "var(--surface-sunken)",
            borderRadius: "var(--r-md)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 13,
          }}
        >
          <span className="muted">Tipo de orden sugerida:</span>
          <span className="mono" style={{ fontWeight: 500 }}>
            {rec.tipo_orden_sugerida}
          </span>
        </div>
      )}
      <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--ink-3)", fontStyle: "italic" }}>
        Una vez ejecutada la OS, sube el resultado por chat (lectura del acta,
        AVM, observaciones) para que el motor reanude el análisis.
      </p>
    </div>
  );
}




// Catálogo de descripciones legibles — debe coincidir con el catálogo
// del backend (`gema/expediente/pipeline.py:_DESCRIPCIONES_PLACEHOLDER`).
// Mantener en sync cuando se agreguen nuevos placeholders al backend.




// Formulario inline movido al chat conversacional .
// Se conserva por si en el futuro reabrimos el flujo de aporte manual.








function AuditLogSSPD({ expedienteId }: { expedienteId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["audit-log", expedienteId],
    queryFn: () => obtenerAuditLog(expedienteId),
    enabled: !!expedienteId,
    refetchInterval: 30_000,
  });
  if (isLoading) return null;
  if (!data || data.total === 0) return null;
  return (
    <div style={{ maxWidth: 820 }}>
      <div
        className="row row-2"
        style={{ marginBottom: 16, fontSize: 13, color: "var(--ink-3)" }}
      >
        <span>
          <strong style={{ color: "var(--ink)", fontWeight: 500 }}>{data.total}</strong>{" "}
          evento(s)
        </span>
        <span>·</span>
        <span
          className={data.cadena_integra ? "badge badge--green" : "badge badge--red"}
          title={
            data.cadena_integra
              ? "Cadena de hashes íntegra: ningún evento fue alterado."
              : "⚠ Cadena rota: algún evento del log fue modificado fuera del flujo."
          }
        >
          <span className="dot" />
          {data.cadena_integra ? "Cadena íntegra" : "Cadena rota"}
        </span>
      </div>
      <ol
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          borderLeft: "1px solid var(--border)",
          marginLeft: 8,
          paddingLeft: 0,
        }}
      >
        {data.eventos.map((ev) => (
          <EventoLi key={ev.evento_id} ev={ev} />
        ))}
      </ol>
      <p
        style={{
          fontSize: 12,
          color: "var(--ink-3)",
          fontStyle: "italic",
          marginTop: 14,
        }}
      >
        Append-only log para auditoría SSPD. Cada evento incluye un hash
        SHA-256 encadenado al anterior; la integridad se verifica
        recalculando los hashes.
      </p>
    </div>
  );
}

function EventoLi({ ev }: { ev: EventoAuditoriaItem }) {
  const colorPorTipo: Record<string, string> = {
    CREADO_DESDE_CRM: "var(--blue)",
    CREADO_DESDE_INTAKE: "var(--blue)",
    INGESTADO_AUTOMATICO: "var(--blue)",
    MERGED_PDF: "var(--amber)",
    DECISION_EMITIDA: "var(--emerald)",
    APROBADO: "var(--emerald-deep)",
    RECHAZADO: "var(--red-deep)",
    INFO_APORTADA: "var(--violet)",
    CAMBIO_DECISION: "var(--violet)",
    CARTA_REFORMULADA: "var(--violet)",
    CONSULTA_CHAT: "var(--ink-3)",
  };
  const color = colorPorTipo[ev.tipo_evento] ?? "var(--ink-3)";
  const fecha = new Date(ev.timestamp_utc).toLocaleString("es-CO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return (
    <li
      style={{
        position: "relative",
        marginLeft: 22,
        marginBottom: 18,
        paddingLeft: 6,
      }}
    >
      <span
        style={{
          position: "absolute",
          left: -27,
          top: 6,
          width: 10,
          height: 10,
          borderRadius: 999,
          background: color,
          border: "2px solid var(--surface)",
          boxShadow: "0 0 0 1px var(--border)",
        }}
      />
      <div className="row row-2" style={{ fontSize: 12, color: "var(--ink-3)" }}>
        <span className="mono">{fecha}</span>
        <span>·</span>
        <span className="mono">#{ev.secuencia}</span>
        <span>·</span>
        <span>{ev.actor}</span>
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: "var(--ink)",
          marginTop: 4,
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 11,
            background: "var(--surface-sunken)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: "1px 6px",
            color: "var(--ink-2)",
          }}
        >
          {ev.tipo_evento}
        </span>
        <span>{ev.descripcion}</span>
      </div>
      {ev.hash_evento && (
        <div
          className="mono"
          style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}
          title="hash SHA-256 (primeros 16 chars)"
        >
          🔗 {ev.hash_evento.slice(0, 16)}…
        </div>
      )}
    </li>
  );
}



// ─── Preview FIEL del DOCX con docx-preview ─────────────────── //
// Fetch del Blob del endpoint /carta.docx y render in-place. El
// resultado es visualmente idéntico al .docx que el operador descarga
// (membrete, tabla del Art 155, cláusulas específicas de la plantilla
// seleccionada). Si docx-preview falla por cualquier razón, cae al
// render de texto plano del `proyeccion_carta` del backend como
// fallback robusto.

